import { useState, useMemo } from "react"
import AwardCard from "./AwardCard"
import AwardModal from "./AwardModal"
import { KINDS } from "./awardKinds"

const normalize = (value) => (value || "").toString().toLowerCase()

// Ukrainian plural for "нагорода": 1 → нагорода, 2–4 → нагороди, інше → нагород.
const awardsWord = (count) => {
  const mod100 = count % 100
  const mod10 = count % 10
  if (mod100 >= 11 && mod100 <= 14) return "нагород"
  if (mod10 === 1) return "нагорода"
  if (mod10 >= 2 && mod10 <= 4) return "нагороди"
  return "нагород"
}

// The whole awards wall. Receives awards from the server (via the Stimulus
// mount) and owns: which award is open (modal), the active kind filter tab, and
// an instant client-side search over direction + kind.
const AwardsIndex = ({ awards }) => {
  const [selected, setSelected] = useState(null)
  const [kind, setKind] = useState("all")
  const [query, setQuery] = useState("")

  // Only show filter tabs for kinds that actually appear in the collection.
  const presentKinds = useMemo(
    () => KINDS.filter((k) => awards.some((a) => a.kind === k.code)),
    [awards]
  )

  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    return awards.filter((award) => {
      if (kind !== "all" && award.kind !== kind) return false
      if (!q) return true
      return `${normalize(award.direction_label)} ${normalize(award.kind_label)}`.includes(q)
    })
  }, [awards, kind, q])

  return (
    <div className="journal">
      <div className="journal__content">
        <header className="journal-head">
          <h1>Мої нагороди</h1>
          <p className="journal-head__sub">медалі, кубки, дипломи та грамоти — усе в одному місці</p>
          <svg className="journal-head__rule" viewBox="0 0 220 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 7 C 40 1, 60 11, 96 6 S 160 1, 216 6" />
          </svg>
          {awards.length > 0 && (
            <p className="journal-head__count">
              {q || kind !== "all" ? (
                <>Знайдено <strong>{filtered.length}</strong> із {awards.length} {awardsWord(awards.length)}</>
              ) : (
                <><strong>{awards.length}</strong> {awardsWord(awards.length)} у колекції</>
              )}
            </p>
          )}
        </header>

        {awards.length > 0 && (
          <>
            {presentKinds.length > 0 && (
              <div className="award-filters" role="tablist" aria-label="Фільтр за типом">
                <button
                  type="button"
                  role="tab"
                  aria-selected={kind === "all"}
                  className={`award-filter${kind === "all" ? " is-active" : ""}`}
                  onClick={() => setKind("all")}
                >
                  усі
                </button>
                {presentKinds.map((k) => (
                  <button
                    type="button"
                    key={k.code}
                    role="tab"
                    aria-selected={kind === k.code}
                    className={`award-filter${kind === k.code ? " is-active" : ""}`}
                    onClick={() => setKind(k.code)}
                  >
                    <i className={`fa-solid ${k.icon}`} aria-hidden="true"></i> {k.many}
                  </button>
                ))}
              </div>
            )}

            <div className="journal-toolbar">
              <div className="journal-search">
                <span className="journal-search__mag" aria-hidden="true">⌕</span>
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="знайти нагороду…"
                  aria-label="Пошук нагород"
                />
                {query && (
                  <button
                    type="button"
                    className="journal-search__clear"
                    onClick={() => setQuery("")}
                    aria-label="Очистити пошук"
                  >
                    ×
                  </button>
                )}
              </div>
              <a className="journal-add" href="/awards/new">✎ додати нагороду</a>
            </div>
          </>
        )}

        {awards.length === 0 ? (
          <div className="journal-empty">
            <h2>Колекція поки порожня</h2>
            <p>Додай першу медаль, кубок, диплом чи грамоту — і вона стане тут.</p>
            <a className="journal-add journal-empty__add" href="/awards/new">✎ додати нагороду</a>
          </div>
        ) : filtered.length > 0 ? (
          <section className="award-wall">
            {filtered.map((award) => (
              <AwardCard key={award.id} award={award} onOpen={() => setSelected(award)} />
            ))}
            {!q && kind === "all" && (
              <a className="frame frame--ghost" href="/awards/new">
                <span className="frame__body">
                  <span className="frame__ghost-label">＋<br />нова нагорода</span>
                </span>
              </a>
            )}
          </section>
        ) : (
          <div className="journal-empty">
            <h2>Нічого не знайшлося</h2>
            <p>За цим фільтром нагород немає. Спробуй інакше.</p>
          </div>
        )}
      </div>

      <AwardModal award={selected} onClose={() => setSelected(null)} />
    </div>
  )
}

export default AwardsIndex
